import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { liveQuery } from 'dexie';
import { Farm } from '../types';
import { db } from './db';
import { useAuth } from './authContext';
interface FarmContextValue {
  activeFarm: Farm | null;
  activeFarmId: string | null;
  setActiveFarmId: (farmId: string | null) => Promise<void>;
  refreshActiveFarm: () => Promise<void>;
}
const FarmContext = createContext<FarmContextValue | undefined>(undefined);
export const useFarm = () => {
  const ctx = useContext(FarmContext);
  if (!ctx) throw new Error('useFarm must be used within FarmProvider');
  return ctx;
};
export const FarmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const database = db;
  const [activeFarm, setActiveFarm] = useState<Farm | null>(null);
  const refreshActiveFarm = useCallback(async () => {
    const stored = await database.workspaceMeta.get('active-farm');
    const farms = (await database.farms.toArray()).filter(farm => !farm.deleted_at);
    const farm = farms.find(farm => farm.farm_id === stored?.value) || farms.find(farm => farm.farm_id !== 'demo-farm') || farms[0] || null;
    if (database === db) setActiveFarm(farm);
  }, [database]);
  const setActiveFarmId = useCallback(async (farmId: string | null) => {
    if (database !== db) return;
    await database.workspaceMeta.put({ key: 'active-farm', value: farmId });
    await refreshActiveFarm();
  }, [database, refreshActiveFarm]);
  useEffect(() => {
    if (!user) return;
    const subscription = liveQuery(refreshActiveFarm).subscribe({ error: console.error });
    return () => subscription.unsubscribe();
  }, [user?.email, refreshActiveFarm]);
  return <FarmContext.Provider value={{ activeFarm, activeFarmId: activeFarm?.farm_id || null, setActiveFarmId, refreshActiveFarm }}>{children}</FarmContext.Provider>;
};
